/**
 * @file WorkspaceAccessPage.tsx
 * @description US2 — Workspace Access Matrix page with KPI cards, filters, table, CSV export
 * @feature 142-governance-compliance-dashboard
 * @constitution v1.12.0
 * @note Conformed to Spec 197 design blueprint (Constitution XLV) — kit + tokens only.
 */

import React, { useState, useMemo } from 'react';
import {
  Users,
  ShieldAlert,
  AlertTriangle,
  Bot,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  Database,
  ShieldCheck,
} from 'lucide-react';
import { useWorkspaceAccess, useWorkspaceAccessSummary, downloadCsv } from '../../../services/operations/governanceService';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  FilterBar,
  EmptyState,
  ErrorState,
  LoadingState,
  Extended,
  QuickGlance,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import type { WorkspaceAccessEntry } from '../../../types/governance';

const WorkspaceAccessPage: React.FC = () => {
  const [workspaceSearch, setWorkspaceSearch] = useState('');
  const [principalSearch, setPrincipalSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const filters = useMemo(() => ({
    workspace: workspaceSearch || undefined,
    principal: principalSearch || undefined,
    role: roleFilter !== 'all' ? roleFilter : undefined,
    principalType: typeFilter !== 'all' ? typeFilter : undefined,
    page,
    pageSize,
  }), [workspaceSearch, principalSearch, roleFilter, typeFilter, page, pageSize]);

  const { data: accessData, isLoading, error, refetch } = useWorkspaceAccess(filters);
  const { data: summaryData } = useWorkspaceAccessSummary();

  // Rows for the current server page; DataTable provides click-to-sort over these
  // (sorting is intentionally per-page, matching the original behavior).
  const entries = accessData?.entries ?? [];

  const handleExportCsv = () => {
    if (!entries.length) return;
    const date = new Date().toISOString().split('T')[0];
    let csv = 'Workspace,Principal,Type,Role,Collected\n';
    entries.forEach((e) => {
      csv += `"${e.workspaceName}","${e.principalName}","${e.principalType}","${e.role}","${e.snapshotDate}"\n`;
    });
    downloadCsv(csv, `workspace-access-${date}.csv`);
  };

  const isEmpty = !isLoading && summaryData?.totalEntries === 0;
  const totalPages = accessData?.pagination?.totalPages ?? 1;
  const totalRows = accessData?.pagination?.total ?? 0;

  const columns: DataTableColumn<WorkspaceAccessEntry>[] = [
    {
      key: 'workspaceName',
      header: 'Workspace',
      width: '28%',
      cellClassName: 'font-medium text-[var(--color-text-primary)]',
    },
    {
      key: 'principalName',
      header: 'Principal',
      width: '28%',
    },
    {
      key: 'principalType',
      header: 'Type',
      width: '14%',
      sortValue: (e) => e.principalType,
      render: (e) => (
        <Badge
          variant={
            e.principalType === 'SP' ? 'primary' : e.principalType === 'Group' ? 'info' : 'secondary'
          }
        >
          {e.principalType}
        </Badge>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      width: '16%',
      sortValue: (e) => e.role,
      render: (e) =>
        e.role === 'Admin' ? (
          <Badge variant="warning" leftIcon={<ShieldAlert className="h-3 w-3" />}>
            Admin
          </Badge>
        ) : (
          <span className="text-[var(--color-text-secondary)]">{e.role}</span>
        ),
    },
    {
      key: 'snapshotDate',
      header: 'Collected',
      width: '14%',
      sortValue: (e) => e.snapshotDate ?? '',
      render: (e) => (
        <span className="text-[var(--color-text-secondary)]">{e.snapshotDate || '—'}</span>
      ),
    },
  ];

  return (
    <PageLayout
      title="Workspace Access"
      description="Cross-workspace user and service principal access audit"
      icon={<ShieldCheck className="h-6 w-6" />}
            helpId="governance.workspace-access"
            onRefresh={() => { void refetch(); }}
            isRefreshing={isLoading}
            actions={
        <>
          <Button leftIcon={<Download className="h-4 w-4" />} onClick={handleExportCsv} disabled={isEmpty}>
            Export CSV
          </Button>
        </>
      }
    >
      {error ? (
        <ErrorState
          title="Failed to load workspace access"
          message={error instanceof Error ? error.message : 'Unknown error'}
          onRetry={() => refetch()}
        />
      ) : (
        <>
          {/* KPI tiles */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Total Entries"
              value={isEmpty ? '—' : summaryData?.totalEntries ?? '—'}
              icon={<Users className="h-4 w-4" />}
            />
            <StatCard
              label="Admins"
              value={isEmpty ? '—' : summaryData?.adminCount ?? '—'}
              icon={<ShieldAlert className="h-4 w-4" />}
            />
            <StatCard
              label="Service Principals"
              value={isEmpty ? '—' : summaryData?.spCount ?? '—'}
              icon={<Bot className="h-4 w-4" />}
            />
            <StatCard
              label="Accounts >10 WSes"
              value={isEmpty ? '—' : summaryData?.overPermissionedCount ?? '—'}
              valueClassName={
                (summaryData?.overPermissionedCount ?? 0) > 0 ? 'text-[var(--color-status-warning)]' : undefined
              }
              icon={<AlertTriangle className="h-4 w-4" />}
            />
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the full details.
            </Card>
          </QuickGlance>

          {isLoading ? (
            <Card className="p-4">
              <LoadingState rows={8} />
            </Card>
          ) : isEmpty ? (
            <EmptyState
              icon={<Database className="h-10 w-10" />}
              title="No governance data collected yet"
              message="Governance collection requires Fabric Admin API access. Contact your admin to enable the governance collector."
            />
          ) : (
            <Extended>
              {/* Filters */}
              <FilterBar
                trailing={
                  <span className="text-sm text-[var(--color-text-secondary)]">
                    {totalRows} total
                  </span>
                }
              >
                <Input
                  placeholder="Search workspace..."
                  value={workspaceSearch}
                  onChange={(e) => { setWorkspaceSearch(e.target.value); setPage(1); }}
                  leftAddon={<Search className="h-4 w-4" />}
                  className="w-full sm:w-64"
                />
                <Input
                  placeholder="Search principal..."
                  value={principalSearch}
                  onChange={(e) => { setPrincipalSearch(e.target.value); setPage(1); }}
                  leftAddon={<Search className="h-4 w-4" />}
                  className="w-full sm:w-64"
                />
                <Select
                  label="Role"
                  value={roleFilter}
                  onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
                  options={[
                    { value: 'all', label: 'All Roles' },
                    { value: 'Admin', label: 'Admin' },
                    { value: 'Member', label: 'Member' },
                    { value: 'Contributor', label: 'Contributor' },
                    { value: 'Viewer', label: 'Viewer' },
                  ]}
                />
                <Select
                  label="Type"
                  value={typeFilter}
                  onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                  options={[
                    { value: 'all', label: 'All Types' },
                    { value: 'User', label: 'User' },
                    { value: 'App', label: 'SP' },
                    { value: 'Group', label: 'Group' },
                  ]}
                />
              </FilterBar>

              {/* Table — server-paginated, so DataTable's built-in pager is suppressed
                  and the explicit Previous/Next controls below own navigation. */}
              <Card padding="none">
                <DataTable
                  columns={columns}
                  rows={entries}
                  rowKey={(e, i) => `${e.identifier}-${e.workspaceName}-${i}`}
                  minWidth="44rem"
                  showPagination={false}
                  emptyMessage="No workspace access entries found"
                />
              </Card>

              {/* Server-side pager */}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-sm text-[var(--color-text-secondary)]">
                  Page {page} of {totalPages} ({totalRows} total)
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<ChevronLeft className="h-4 w-4" />}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    rightIcon={<ChevronRight className="h-4 w-4" />}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Extended>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default WorkspaceAccessPage;
